<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) + " " + $tc("dmp.rules", 1)
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide"/>
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Description -->
          <validation-provider
            #default="validationContext"
            :name="$t('dmp.description')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('dmp.description')"
              label-for="description"
            >
              <b-form-input
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Device type -->
          <validation-provider
            #default="validationContext"
            :name="$t('dmp.deviceType')"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('dmp.deviceType')"
              label-for="device_type"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.device_type"
                :options="['area', 'output', 'system', 'zone', 'zone24h']"
                input-id="device_type"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Device id -->
          <validation-provider #default="validationContext" name="device_id">
            <b-form-group
              class="text-capitalize"
              :label="$t('dmp.deviceId')"
              label-for="device_id"
            >
              <b-form-input
                id="device_id"
                v-model="itemData.device_id"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Event definition -->
          <validation-provider
            #default="validationContext"
            :name="$t('dmp.eventDefinition')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('dmp.eventDefinition')"
              label-for="event_definition"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.event_definition"
                :options="eventDefinitionOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                @input="setEventTypes"
                input-id="event_definition"
              />

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Event type -->
          <validation-provider #default="validationContext" :name="$t('dmp.eventType')">
            <b-form-group
              class="text-capitalize"
              :label="$t('dmp.eventType')"
              label-for="event_type"
              :state="getValidationState(validationContext)"
            >
              <v-select
                label="description"
                v-model="itemData.event_type"
                :options="eventTypes"
                :clearable="true"
                :reduce="(val) => val.code"
                input-id="event_type"
              />

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Type alarm -->
          <validation-provider
            #default="validationContext"
            :name="$t('dmp.typeAlarm')"
            rules="required"
          >
            <b-form-group
              :label="$t('dmp.typeAlarm')"
              label-for="id_type_alarm"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_type_alarm"
                :options="typeAlarmOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                @input="(val) => $emit('update:typeAlarmFilter', val)"
                input-id="id_type_alarm"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Check Remote User -->
          <validation-provider
            #default="validationContext"
            name="checkRemoteUser"
          >
            <b-form-group
              :label="$t('dmp.checkRemoteUser')"
              label-for="checkRemoteUser"
              :state="getValidationState(validationContext)"
            >
              <b-form-checkbox
                v-model="itemData.check_remote_user"
                name="chek-remote-user"
                input-id="checkRemoteUser"
                switch
              >
              </b-form-checkbox>
            </b-form-group>
          </validation-provider>

          <!-- Device tag -->
          <validation-provider #default="validationContext" :name="$t('dmp.tags')">
            <b-form-group
              class="text-capitalize"
              :label="$t('dmp.tags')"
              label-for="id_device_tag"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_device_tag"
                :options="tagOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                label="name"
                input-id="id_device_tag"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Icon color -->
          <validation-provider #default="validationContext" name="color_icon">
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.color_icon')"
              label-for="color_icon"
            >
              <b-form-input
                id="color_icon"
                v-model="itemData.color_icon"
                :state="getValidationState(validationContext)"
                trim
                type="color"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Icon -->
          <validation-provider #default="validationContext" name="icon">
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.icon')"
              label-for="icon"
            >
              <!-- Icon Selector Button -->
              <div
                class="icon-selector-btn border rounded p-1 d-flex align-items-center justify-content-between cursor-pointer"
                @click="showIconPicker = !showIconPicker"
              >
                <div class="d-flex align-items-center">
                  <custom-icon
                    v-if="itemData.icon"
                    :name="itemData.icon"
                    size="28"
                    :color="itemData.color_icon"
                  />
                  <feather-icon v-else icon="ImageIcon" size="28" class="text-muted"/>
                  <span class="ml-2">{{
                      itemData.icon || $t("types_alarms.no_icon")
                    }}</span>
                </div>
                <!-- Clear Icon Button -->
                <div>
                  <b-button
                    v-if="itemData.icon"
                    variant="outline-danger"
                    size="sm"
                    class="mr-1"
                    @click="clearIcon"
                  >
                    <feather-icon icon="XIcon" size="14" class=""/>
                  </b-button>
                  <feather-icon
                    :icon="showIconPicker ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="18"
                  />
                </div>
              </div>

              <!-- Icon Picker Dropdown -->
              <div v-if="showIconPicker" class="icon-picker-dropdown mt-2">
                <!-- Search filter -->
                <b-form-input
                  v-model="iconSearch"
                  :placeholder="$t('table.search') + '...'"
                  class="mb-2"
                  trim
                />

                <!-- Icon Grid -->
                <div class="icon-grid-container border rounded p-2">
                  <div class="icon-grid">
                    <div
                      v-for="icon in filteredIcons"
                      :key="icon"
                      class="icon-grid-item"
                      :class="{ selected: itemData.icon === icon }"
                      @click="selectIcon(icon)"
                      :title="icon"
                    >
                      <custom-icon :name="icon" size="24" :color="itemData.color_icon"/>
                    </div>
                  </div>
                  <div
                    v-if="filteredIcons.length === 0"
                    class="text-center text-muted py-2"
                  >
                    {{ $t("no_record_found") }}
                  </div>
                </div>
              </div>

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Dispatch alarm -->
          <validation-provider
            #default="validationContext"
            name="dispatchAlarm"
            rules="required"
          >
            <b-form-group
              :label="$t('dmp.dispatchAlarm')"
              label-for="dispatchAlarm"
              :state="getValidationState(validationContext)"
            >
              <b-form-checkbox
                v-model="itemData.dispatch_alarm"
                name="chek-dispatch-alarm"
                input-id="dispatchAlarm"
                switch
              >
              </b-form-checkbox>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BInputGroupAppend,
} from "bootstrap-vue";
import {ValidationProvider, ValidationObserver} from "vee-validate";
import vSelect from "vue-select";
import {computed, ref, onMounted} from "@vue/composition-api";
import {required, alphaNum, email, positive, required_if} from "@validations";
import {useToast} from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import {BANK_ICON_NAMES} from "@/assets/svg/CustomIcons";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroupAppend,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    idDmpProfile: {
      type: String | Number,
    },
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    eventDefinitionOptions: {
      type: Array,
      default: () => [],
    },
    typeAlarmOptions: {
      type: Array,
      default: () => [],
    },
    tagOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, {emit}) {
    //COMPOSABLES
    const toast = useToast();

    //DATA
    const blankData = {
      id_dmp_profile: parseInt(props.idDmpProfile),
      description: null,
      event_definition: null,
      event_type: null,
      device_type: null,
      device_id: null,
      device_name: null,
      id_type_alarm: null,
      id_device_tag: null,
      check_remote_user: false,
      icon: null,
      color_icon: null,
      dispatch_alarm: true,
    };

    const eventTypes = ref([]);

    const iconOptions = BANK_ICON_NAMES;
    const iconSearch = ref("");
    const showIconPicker = ref(false);

    const filteredIcons = computed(() => {
      if (!iconSearch.value) return iconOptions;
      const search = iconSearch.value.toLowerCase();
      return iconOptions.filter((name) => name.toLowerCase().includes(search));
    });

    const selectIcon = (icon) => {
      itemData.value.icon = icon;
      showIconPicker.value = false;
      iconSearch.value = "";
    };

    const clearIcon = () => {
      itemData.value.icon = null;
      showIconPicker.value = false;
      iconSearch.value = "";
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const onSubmit = async () => {
      let dispatch;

      if (props.isAdd) {
        dispatch = store.dispatch("dmp_rules/add", itemData.value);
      } else {
        const req = {
          id: itemData.value.id,
          params: itemData.value,
        };
        dispatch = store.dispatch("dmp_rules/edit", req);
      }

      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });

            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const {refFormObserver, getValidationState, resetForm} = formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = {...blankData, ...props.itemEdit};

        const eventDefinition = props.eventDefinitionOptions.find((etd) => etd.value === itemData.value.event_definition);

        if (eventDefinition) {
          eventTypes.value = eventDefinition.related_events;
        }
      }
    };

    const setEventTypes = (value) => {
      const eventDefinition = props.eventDefinitionOptions.find((etd) => etd.value === value);

      itemData.value.event_type = null;

      if (eventDefinition) {
        eventTypes.value = eventDefinition.related_events || [];
      }
    };

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      setEventTypes,
      eventTypes,
      iconOptions,
      iconSearch,
      filteredIcons,
      showIconPicker,
      selectIcon,
      clearIcon,
    };
  },
};
</script>

<style scoped>
.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(44px, 1fr));
  gap: 8px;
}

.icon-grid:before {
  content: unset;
}

.icon-grid-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.icon-grid-item.selected img {
  filter: brightness(0) invert(1);
}

.icon-grid-container::-webkit-scrollbar {
  width: 6px;
}

.icon-grid-container::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}

.icon-grid-container::-webkit-scrollbar-thumb:hover {
  background-color: #999;
}

.icon-selector-btn {
  transition: all 0.2s ease;
}

.icon-grid-container {
  max-height: 250px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
